import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

function useCountUp(target, duration = 1600) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const triggered = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered.current) {
          triggered.current = true;
          const start = performance.now();
          const animate = (t) => {
            const p = Math.min((t - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(target * eased));
            if (p < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);
  return [val, ref];
}

const stats = [
  { value: 100, suffix: "%", label: "Conformité visée", sub: "ISO 27001 / NIS2 / RGPD" },
  { value: 24, suffix: "/7", label: "Veille & SOC", sub: "Détection & réponse continue" },
  { value: 2, suffix: "", label: "Continents", sub: "France 🇫🇷 · Sénégal 🇸🇳" },
  { value: 15, suffix: "+", label: "Standards maîtrisés", sub: "EBIOS RM, ISO 27005, NIST…" },
];

function StatCard({ stat, idx }) {
  const [v, ref] = useCountUp(stat.value);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: idx * 0.08 }}
      data-testid={`stat-card-${idx}`}
      className="relative p-7 rounded-2xl bg-[#0C1410] border border-[#1A2E22] hover:border-emerald-500/40 transition-colors group"
    >
      <div className="absolute top-4 right-4 font-mono text-[10px] text-gray-600">
        0{idx + 1}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-display text-5xl md:text-6xl font-bold text-white tabular-nums">
          {v}
        </span>
        <span className="font-display text-3xl text-emerald-400 font-bold">
          {stat.suffix}
        </span>
      </div>
      <div className="mt-3 text-white font-medium">{stat.label}</div>
      <div className="text-sm text-gray-500 mt-1">{stat.sub}</div>
    </motion.div>
  );
}

export default function StatsSection() {
  return (
    <section id="stats" className="relative py-24 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs text-emerald-400 mb-4 tracking-widest">
              // NOTRE MISSION
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold leading-[1.1] tracking-tight">
              Vous aider à <span className="italic text-emerald-400">maîtriser</span> vos risques,
              <br />
              protéger vos actifs, élever votre maturité cyber.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-gray-400 leading-relaxed">
              Une approche pragmatique combinant{" "}
              <span className="text-white">gouvernance, conformité</span> et{" "}
              <span className="text-white">défense opérationnelle</span> — de la cartographie
              des risques jusqu'au déploiement de votre SOC et SMSI.
            </p>
          </div>
        </div>

        <div data-testid="stats-section" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((s, i) => (
            <StatCard key={i} stat={s} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
